// src/services/api.js (recommendations)
import axios from 'axios';

// Phase 1 auth: browser auth is cookie-based. We intentionally do NOT send
// X-API-Key from the UI — that key used to be baked into the JS bundle and
// was publicly readable. Edge agent and other machine clients still use
// X-API-Key; the backend middleware short-circuits this check when the
// access_token cookie is present.
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: 90000, // 90 seconds for non-streaming fallback
});

const readCsrfCookie = () => {
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
};

api.interceptors.request.use(
  (config) => {
    const method = (config.method || 'get').toUpperCase();
    const unsafe = method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS' && method !== 'TRACE';
    if (unsafe) {
      const csrf = readCsrfCookie();
      if (csrf && !config.headers['X-CSRF-Token']) {
        config.headers['X-CSRF-Token'] = csrf;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for handling common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Server responded with an error status code
    if (error.response) {
      // Handle 401 Unauthorized errors by redirecting to login
      if (error.response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
      
      // Handle specific error codes with custom handling
      switch (error.response.status) {
        case 403:
          console.error('Permission denied:', error.response.data);
          break;
        case 429:
          console.error('Rate limit exceeded. Please try again later.');
          break;
        case 500:
        case 502:
        case 503:
        case 504:
          console.error('Server error. Please try again later or contact support.');
          break;
        default:
          break;
      }
    } 
    // The request was made but no response was received
    else if (error.request) {
      console.error('No response received from the server. Please check your network connection.');
    } 
    // Something happened in setting up the request
    else {
      console.error('Error setting up the request:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// FastAPI's default validation-error handler returns `detail` as an array of
// {type, loc, msg, input} objects, not a string — rendering it directly as a
// React child throws "Objects are not valid as a React child". Our own
// handlers return a plain string, so this normalizes either shape to text.
export function errorDetail(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).join('; ');
  return fallback;
}

export default api;
